import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Inbox, Clock, CheckCircle, 
  AlertCircle, ChevronLeft, User, Filter, Search,
  MoreVertical, Archive, Trash2, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const statusColors = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const statusIcons = {
  open: <AlertCircle className="w-3 h-3" />,
  pending: <Clock className="w-3 h-3" />,
  resolved: <CheckCircle className="w-3 h-3" />,
  closed: <Archive className="w-3 h-3" />
};

export default function HRInbox() {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  useEffect(() => {
    filterConversations();
  }, [conversations, searchQuery, statusFilter]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/hr-support/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterConversations = () => {
    let filtered = [...conversations];
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.employee_name?.toLowerCase().includes(query) ||
        c.employee_email?.toLowerCase().includes(query) ||
        c.subject?.toLowerCase().includes(query) ||
        c.messages?.some(m => m.message.toLowerCase().includes(query))
      );
    }
    
    setFilteredConversations(filtered);
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    
    // Mark as read
    if (conv.unread_by_hr) {
      await axios.put(`${API}/hr-support/conversations/${conv.conversation_id}/read`);
      fetchConversations();
    }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/hr-support/conversations/${selectedConversation.conversation_id}/messages`,
        { message: newMessage }
      );
      
      // Refresh conversation
      const response = await axios.get(
        `${API}/hr-support/conversations/${selectedConversation.conversation_id}`
      );
      setSelectedConversation(response.data.conversation);
      setNewMessage('');
      await fetchConversations();
      toast.success('Reply sent');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (conversationId, status) => {
    try {
      await axios.put(
        `${API}/hr-support/conversations/${conversationId}/status?status=${status}`
      );
      toast.success(`Marked as ${status}`);
      fetchConversations();
      
      if (selectedConversation?.conversation_id === conversationId) {
        setSelectedConversation(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = conversations.filter(c => c.unread_by_hr).length;

  return (
    <div className="space-y-6" data-testid="hr-inbox">
      {/* Header — Iter87: light-mode-safe colors so the title and
          subtitle remain visible regardless of the active theme.
          Reported by Ankur 04-Jun-2026. */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Inbox className="w-7 h-7 text-emerald-500 dark:text-emerald-400" />
            HR Inbox
            {unreadCount > 0 && (
              <Badge className="bg-emerald-500 text-white">{unreadCount} new</Badge>
            )}
          </h1>
          <p className="text-slate-600 dark:text-gray-400 mt-1">Manage employee support conversations</p>
        </div>
      </div>

      <div className="h-[calc(100vh-200px)] flex bg-[#0a1628] rounded-xl border border-white/10 overflow-hidden">
        {/* Conversations List */}
        <div className={`w-96 border-r border-white/10 flex flex-col ${selectedConversation ? 'hidden lg:flex' : 'flex'}`}>
          {/* Filters */}
          <div className="p-4 border-b border-white/10 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'open', 'pending', 'resolved', 'closed'].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? 'default' : 'ghost'}
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-emerald-500' : 'text-gray-400'}
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => selectConversation(conv)}
                  className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id ? 'bg-white/10' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-medium">
                          {conv.employee_name?.charAt(0) || 'E'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white font-medium flex items-center gap-2">
                          {conv.employee_name}
                          {conv.unread_by_hr && (
                            <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                          )}
                        </p>
                        <p className="text-xs text-gray-400">{conv.employee_department}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatTime(conv.updated_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 truncate mt-2 ml-13">
                    {conv.subject}
                  </p>
                  <div className="flex items-center justify-between mt-2 ml-13">
                    <Badge className={`text-xs ${statusColors[conv.status]}`}>
                      {statusIcons[conv.status]}
                      <span className="ml-1 capitalize">{conv.status}</span>
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {conv.messages?.length || 0} messages
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden lg:flex' : 'flex'}`}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedConversation(null)}
                    className="lg:hidden"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-emerald-400 font-medium">
                      {selectedConversation.employee_name?.charAt(0) || 'E'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{selectedConversation.employee_name}</h3>
                    <p className="text-xs text-gray-400">
                      {selectedConversation.employee_email} • {selectedConversation.employee_department}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${statusColors[selectedConversation.status]}`}>
                    {statusIcons[selectedConversation.status]}
                    <span className="ml-1 capitalize">{selectedConversation.status}</span>
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" data-testid="hr-inbox-menu-trigger">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    {/* Iter82 — Wider explicit width + sideOffset so the
                        menu doesn't get clipped by the right edge of the
                        chat panel. Reported by Ankur 03-Jun-2026. */}
                    <DropdownMenuContent
                      align="end"
                      sideOffset={6}
                      /* Iter56b (Ankur 25-Jun-2026 Bug 2) — Force the dark
                         navy + an inline style fallback so the menu is
                         readable even in light mode. The shadcn default
                         `bg-white` was winning over the arbitrary
                         `bg-[#1a2942]` class (Tailwind JIT did not pick up
                         the literal), so items with `text-white` rendered
                         on a white background → invisible dropdown. */
                      className="!bg-[#1a2942] border-white/10 min-w-[220px] w-56 z-50 shadow-xl"
                      style={{ backgroundColor: '#1a2942' }}
                      data-testid="hr-inbox-menu-content"
                    >
                      <DropdownMenuItem 
                        onClick={() => updateStatus(selectedConversation.conversation_id, 'open')}
                        className="text-white hover:bg-white/10"
                      >
                        <AlertCircle className="w-4 h-4 mr-2 text-blue-400" />
                        Mark as Open
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => updateStatus(selectedConversation.conversation_id, 'pending')}
                        className="text-white hover:bg-white/10"
                      >
                        <Clock className="w-4 h-4 mr-2 text-yellow-400" />
                        Mark as Pending
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => updateStatus(selectedConversation.conversation_id, 'resolved')}
                        className="text-white hover:bg-white/10"
                      >
                        <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                        Mark as Resolved
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => updateStatus(selectedConversation.conversation_id, 'closed')}
                        className="text-white hover:bg-white/10"
                      >
                        <Archive className="w-4 h-4 mr-2 text-gray-400" />
                        Close Conversation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedConversation.messages?.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_type === 'hr' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        msg.sender_type === 'hr'
                          ? 'bg-emerald-500 text-white rounded-br-md'
                          : 'bg-white/10 text-white rounded-bl-md'
                      }`}
                    >
                      {msg.sender_type === 'employee' && (
                        <p className="text-xs text-emerald-400 font-medium mb-1">
                          {msg.sender_name}
                        </p>
                      )}
                      {msg.sender_type === 'hr' && (
                        <p className="text-xs text-white/70 font-medium mb-1">
                          You ({msg.sender_name})
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'hr' ? 'text-white/70' : 'text-gray-400'
                      }`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Box */}
              {selectedConversation.status !== 'closed' && (
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 bg-white/5 border-white/10 text-white"
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                    />
                    <Button 
                      onClick={sendReply}
                      disabled={sending || !newMessage.trim()}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {sending ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* No Selection */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Inbox className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl text-white mb-2">HR Inbox</h3>
                <p className="text-gray-400">Select a conversation to view and reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
